import {Button, CurrencyInput, isCurrency, Tooltip} from 'platform/components';
import {HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useId, useState} from 'react';

import {isNotNil} from 'ramda';

import {Money} from '@dms/api/shared';

import {financingRateSchema} from '../schemas/financingRateSchema';

const REQUIRED_INDICATOR = '* ';

/**
 * The currency of an amount the user cleared, together with the `currency` prop it was recorded
 * under. Keeping the prop next to the remembered currency lets the fallback be derived during
 * render: once the owner hands down a different `currency`, the entry no longer matches and the
 * new prop wins, so no effect is needed to resynchronise it.
 */
interface ClearedAmountCurrency {
  recordedForCurrency: string;
  currency: string;
}

interface MoneyInputFieldProps {
  label: string;
  tooltip?: string;
  value: Money | null;
  currency: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
  onChange: (value: Money | null) => void;
  resetLabel?: string;
  onReset?: () => void;
}

export function MoneyInputField(props: MoneyInputFieldProps) {
  const inputId = useId();
  const [clearedAmountCurrency, setClearedAmountCurrency] = useState<ClearedAmountCurrency | null>(
    null
  );
  // An amount carries its own currency, so that one always wins. Once the amount is cleared the
  // currency of the cleared amount is offered to whatever the user types next, but only while the
  // owner still hands down the same `currency` prop.
  const fallbackCurrency = match(clearedAmountCurrency)
    .with({recordedForCurrency: props.currency}, (cleared) => cleared.currency)
    .otherwise(() => props.currency);
  const currency = props.value?.currency ?? fallbackCurrency;
  const amount = props.value === null ? null : Number(props.value.amount);
  // No form owns this amount, so the rule the financing schemas apply is parsed here directly and
  // the translated message zod produces is surfaced on the input.
  const amountValidation = props.value === null ? null : financingRateSchema.safeParse(amount);
  const errorMessage =
    props.errorMessage ??
    (isNotNil(amountValidation) && !amountValidation.success
      ? amountValidation.error.issues[0]?.message
      : undefined);

  return (
    // The label row is composed locally instead of via `CurrencyInput`'s `labelActions`: the
    // platform Label renders an href-less action as a `Link` (an anchor without `href` is not in
    // the tab order), while the reset action must be keyboard-reachable with real disabled
    // semantics. The row mirrors the platform Label anatomy (typography, spacing, tooltip icon)
    // and renders the action as a real `Button` (`variant="link"`), keeping the designed look.
    <VStack>
      <HStack spacing={4} justify="space-between" align="flex-start">
        <HStack spacing={1}>
          <label htmlFor={inputId}>
            <Text size="xSmall" color="secondary">
              {`${props.isRequired ? REQUIRED_INDICATOR : ''}${props.label}`}
            </Text>
          </label>
          <Show when={props.tooltip}>
            <Tooltip description={props.tooltip}>
              <Icon value="action/help" color="text.secondary" size={4} tabIndex={0} />
            </Tooltip>
          </Show>
        </HStack>
        <Show when={isNotNil(props.onReset) && isNotNil(props.resetLabel)}>
          <Button
            title={props.resetLabel}
            leftIcon="action/replay"
            isDisabled={props.isDisabled}
            size="small"
            variant="link"
            onClick={props.onReset}
          />
        </Show>
      </HStack>
      <Space vertical={1} />
      <CurrencyInput
        id={inputId}
        value={amount}
        currency={isCurrency(currency) ? currency : undefined}
        isDisabled={props.isDisabled}
        isRequired={props.isRequired}
        errorMessage={errorMessage}
        onChange={(value) => {
          if (value === null) {
            setClearedAmountCurrency({recordedForCurrency: props.currency, currency});
            props.onChange(null);
            return;
          }

          props.onChange({
            amount: String(value),
            currency,
          });
        }}
      />
    </VStack>
  );
}
